import {
  Box,
  DateTimeInput,
  Flex,
  Form,
  mdBumps,
  ModalHeader,
  PrimaryButton,
  SingleSelect,
  TertiaryButton,
  Typo,
  xsBumps,
} from '@wrisk/ui-components'
import React, { FunctionComponent, useCallback, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { getExpiryDate, getInceptedDate } from '../../../../../domain'
import { useApiErrorHandlingAsyncCallback } from '../../../../../hooks/auth'
import {
  tEntries,
  TExtends,
  tFormats,
  TKey,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { toDateTime } from '../../../../../util/date'
import { WriskTrans } from '../../../../organisms/trans'
import { AdjustmentModalProps } from './types'
import {
  isBeforeExpiry,
  isOnOrAfterInception,
  isValidDate,
  isWithin30Days,
} from './validation'

const tKey = TKey('components.active-adjustment-modal')

interface AdjustmentModalFormData {
  select: AdjustmentOption
  date: string | undefined
}

enum AdjustmentOption {
  Immediately = 'immediately',
  BackdateToInception = 'backdate-to-inception',
  OtherDate = 'other-date',
}

const tOptions = TExtends(tEntries, 'mtaEffectiveAt')

export const ActiveAdjustmentModal: FunctionComponent<AdjustmentModalProps> = ({
  updatedData,
  policy,
  onCancel,
  onSubmitAdjustment,
}) => {
  const { t } = useWriskTranslation()

  // Create a new error-handling wrapper in this component's context
  const wrappedOnSubmitAdjustment = useApiErrorHandlingAsyncCallback(
    async (data: typeof updatedData, effectiveAt?: string) => {
      // Call the original function but ensure errors are handled in this context
      await onSubmitAdjustment.execute(data, effectiveAt)
    },
  )

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<AdjustmentModalFormData>({
    defaultValues: { select: AdjustmentOption.Immediately },
  })

  const options = useMemo(
    () => ({
      [AdjustmentOption.Immediately]: t(tOptions(AdjustmentOption.Immediately)),
      [AdjustmentOption.BackdateToInception]: t(
        tOptions(AdjustmentOption.BackdateToInception),
      ),
      [AdjustmentOption.OtherDate]: t(tOptions(AdjustmentOption.OtherDate)),
    }),
    [t],
  )

  const earliestYear = getInceptedDate(policy).year
  const latestYear = getExpiryDate(policy).year

  const onSubmit = useCallback(
    (data: AdjustmentModalFormData) => {
      const effectiveAt = (() => {
        switch (data.select) {
          case AdjustmentOption.OtherDate:
            return data.date
          case AdjustmentOption.BackdateToInception:
            return policy.policyDetail.inceptedAt
          case AdjustmentOption.Immediately:
            return undefined
        }
      })()

      void wrappedOnSubmitAdjustment.execute(updatedData, effectiveAt)
    },
    [wrappedOnSubmitAdjustment, policy, updatedData],
  )

  return (
    <Box>
      <ModalHeader header={t(tKey('header'))} />
      <Form data-testid='active-adjustment-form' onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name='select'
          control={control}
          render={({ field: { name, onChange, value } }) => (
            <SingleSelect
              name={name}
              value={value}
              onChange={onChange}
              options={options}
            />
          )}
        />
        {watch('select') === AdjustmentOption.OtherDate && (
          <Box mt={mdBumps}>
            <Controller
              name='date'
              control={control}
              rules={{
                validate: {
                  isValidDate,
                  isOnOrAfterInception: isOnOrAfterInception(
                    policy.policyDetail.inceptedAt,
                  ),
                  isWithin30Days,
                  isBeforeExpiry: isBeforeExpiry(policy.policyDetail.expiredAt),
                },
              }}
              render={({
                field: {
                  // eslint-disable-next-line unused-imports/no-unused-vars
                  ref,
                  ...field
                },
              }) => (
                <DateTimeInput
                  {...field}
                  earliestYear={earliestYear}
                  latestYear={latestYear}
                />
              )}
            />
            {errors.date && (
              <Typo mt={xsBumps} color='textCritical'>
                <WriskTrans
                  t={t}
                  i18nKey={tKey('errors', errors.date.type)}
                  values={{
                    inceptionDate: t(tFormats('datetime.medium'), {
                      value: toDateTime(policy.policyDetail.inceptedAt),
                    }),
                    expiryDate: t(tFormats('datetime.medium'), {
                      value: toDateTime(policy.policyDetail.expiredAt),
                    }),
                  }}
                />
              </Typo>
            )}
          </Box>
        )}
        <Flex width={1} mt={mdBumps}>
          <PrimaryButton
            width={1}
            loading={wrappedOnSubmitAdjustment.loading}
            type='submit'
          >
            {t(tKey('actions.continue'))}
          </PrimaryButton>
          <TertiaryButton width={1} ml={xsBumps} onClick={onCancel}>
            {t(tKey('actions.cancel'))}
          </TertiaryButton>
        </Flex>
      </Form>
    </Box>
  )
}
